import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Order, OrderStatus } from '../types';
import { useOrderStore } from '../stores/useOrderStore';
import { WebSocketProvider } from '../providers/WebSocketProvider';
import { OrderCard } from '../components/OrderCard';
import { playOrderNotification } from '../utils/sound';

const STATUS_COLUMNS: { status: OrderStatus; label: string; icon: string }[] = [
  { status: 'NEW', label: 'New Orders', icon: '🔴' },
  { status: 'PREPARING', label: 'Preparing', icon: '🟡' },
  { status: 'READY', label: 'Ready for Pickup', icon: '🟢' },
  { status: 'COMPLETED', label: 'Completed', icon: '✅' },
];

interface OrdersBoardProps {
  restaurantId: string;
  restaurantName: string;
  onLogout: () => void;
}

export const OrdersBoard: React.FC<OrdersBoardProps> = ({
  restaurantId,
  restaurantName: _restaurantName,
}) => {
  const { orders, loading, error, fetchOrders, addOrUpdateOrder, updateOrderStatus } =
    useOrderStore(restaurantId);

  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(new Set());

  // Track which order IDs have already triggered a sound — shared across
  // WebSocket push AND poll so we never double-chime the same order.
  const notifiedOrderIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    void fetchOrders();
    const interval = setInterval(() => void fetchOrders(), 30_000);
    const onFocus = () => void fetchOrders();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchOrders]);

  // Play bell chime whenever a genuinely NEW order appears — works for both
  // WebSocket push (addOrUpdateOrder) and poll (fetchOrders replacing state).
  useEffect(() => {
    if (isFirstLoad.current) {
      // Seed the set on initial load so we don't chime for pre-existing orders.
      orders.forEach((o) => notifiedOrderIds.current.add(o.orderId));
      isFirstLoad.current = false;
      return;
    }
    let hasNew = false;
    for (const order of orders) {
      if (order.status === 'NEW' && !notifiedOrderIds.current.has(order.orderId)) {
        notifiedOrderIds.current.add(order.orderId);
        setNewOrderIds((prev) => new Set(prev).add(order.orderId));
        setTimeout(() => {
          setNewOrderIds((prev) => { const next = new Set(prev); next.delete(order.orderId); return next; });
        }, 10_000);
        hasNew = true;
      }
    }
    if (hasNew) playOrderNotification();
  }, [orders]);

  const handleOrderCreated = useCallback(
    (order: Order) => addOrUpdateOrder(order),
    [addOrUpdateOrder]
  );

  const handleOrderUpdated = useCallback((order: Order) => addOrUpdateOrder(order), [addOrUpdateOrder]);
  const handleStatusChange = async (orderId: string, status: OrderStatus): Promise<void> => { await updateOrderStatus(orderId, status); };

  const activeOrders = orders.filter((o) => o.status !== 'COMPLETED');
  const grouped = STATUS_COLUMNS.reduce<Record<OrderStatus, Order[]>>(
    (acc, col) => {
      acc[col.status] = orders
        .filter((o) => o.status === col.status)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return acc;
    },
    { NEW: [], PREPARING: [], READY: [], COMPLETED: [] }
  );

  return (
    <WebSocketProvider restaurantId={restaurantId} onOrderCreated={handleOrderCreated} onOrderUpdated={handleOrderUpdated}>
      <div className="bg-gray-950 text-white">
        {/* Mini stats bar */}
        <div className="flex items-center gap-4 px-6 py-3 border-b border-white/5 bg-gray-900/40">
          <span className="text-sm text-gray-400">Active Orders:</span>
          <span className="font-bold text-white">{activeOrders.length}</span>
          <div className="ml-auto flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
            <span className="text-green-400 text-sm font-semibold">Live</span>
            <button onClick={() => void fetchOrders()} className="ml-3 text-gray-500 hover:text-white text-sm px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>
        <main className="px-4 pb-8">
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Loading orders...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="mx-auto max-w-md mt-8 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-center">
              <p className="text-red-400 font-medium">{error}</p>
              <button
                onClick={() => void fetchOrders()}
                className="mt-3 text-sm text-brand-400 hover:text-brand-300 underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Board */}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
              {STATUS_COLUMNS.map(({ status, label, icon }) => (
                <div key={status} className="flex flex-col gap-3">
                  {/* Column Header */}
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <span>{icon}</span>
                      <span className="font-semibold text-sm text-gray-300">{label}</span>
                    </div>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 text-xs font-bold text-gray-300">
                      {grouped[status].length}
                    </span>
                  </div>

                  {/* Column Divider */}
                  <div className="h-px bg-white/5" />

                  {/* Cards */}
                  <div className="space-y-3">
                    {grouped[status].length === 0 ? (
                      <div className="flex items-center justify-center h-28 rounded-xl border border-dashed border-white/10">
                        <p className="text-gray-600 text-sm">No {label.toLowerCase()}</p>
                      </div>
                    ) : (
                      grouped[status].map((order) => (
                        <OrderCard
                          key={order.orderId}
                          order={order}
                          onStatusChange={handleStatusChange}
                          isNew={newOrderIds.has(order.orderId)}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </WebSocketProvider>
  );
};
